import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { gql, useMutation } from '@common/hooks';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';
import { CreateOfferMutation } from 'works/legacy/graphql/offer';
import { OfferFormDetails } from 'works/legacy/components/offer-form-details';
import { getRateByEmploymentType } from '@institutions/services';
import { BillingCardFragment } from '@institutions/graphql';

export function OfferForm(props) {
  const {
    application,
    application: { staffingRequest: request },
    form,
    onRateChange,
    onLoading,
    setIsFormValid,
    initialRate,
  } = props;

  const initialOfferStartDate = moment(request.startDate).isAfter(moment())
    ? moment(request.startDate)
    : null;

  const initialValues = {
    startDate: initialOfferStartDate,
    endDate: moment(request.endDate),
    billingRegularRate: initialRate,
    timeoffDescription: application.timeoffDescription,
    shiftType: request.shiftType,
    shiftsPerWeek: request.shiftsPerWeek,
    hoursPerShift: request.hoursPerShift,
    overtimeThreshold: request.organization.overtimeConfig?.maxRegularHours,
  };

  const handleMutationCompletion = (response) => {
    onLoading?.(false);
    if (response.createOffer.errors) {
      addErrorsToForm(form, response.createOffer.errors);
    } else {
      createMessage('success', 'Offer has been sent!');
      if (props.onFinish) props.onFinish(response.createOffer.offer);
    }
  };

  const [createOffer] = useMutation(CreateOfferMutation, {
    onCompleted: handleMutationCompletion,
    onError: () => {
      onLoading?.(false);
      createMessage('error', 'Failed to send Offer. Please try again.');
    },
    refetchQueries: ['StaffingRequestPageQuery'],
  });

  const handleSubmit = (values) => {
    values['startDate'] = moment(values['startDate']).format('YYYY-MM-DD');
    values['endDate'] = moment(values['endDate']).format('YYYY-MM-DD');

    const { isDeviated, ...input } = values;
    onLoading?.(true);
    createOffer({
      variables: {
        staffingRequestApplicationId: application.id,
        deviated: isDeviated,
        input,
      },
    });
  };

  const handleShiftTypeChange = ({ shiftType }) => {
    if (onRateChange && shiftType) {
      const currentBillingCardRate = getRateByEmploymentType(
        request.billingCard,
        shiftType,
        application.employmentType
      );

      onRateChange(currentBillingCardRate ?? request.billingRegularRate);
    }
  };

  return (
    <OfferFormDetails
      form={form}
      onFinish={handleSubmit}
      onChange={handleShiftTypeChange}
      application={application}
      initialValues={initialValues}
      setIsFormValid={setIsFormValid}
    />
  );
}

OfferForm.propTypes = {
  form: PropTypes.object.isRequired,
  application: PropTypes.shape({
    id: PropTypes.string.isRequired,
    employmentType: PropTypes.string.isRequired,
    canExtendOfferWeekAssignment: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    canIncreaseOfferRate: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    staffingRequest: PropTypes.shape({
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      billingRegularRate: PropTypes.number.isRequired,
      shiftType: PropTypes.string.isRequired,
      shiftsPerWeek: PropTypes.number.isRequired,
      hoursPerShift: PropTypes.number.isRequired,
      staffingRequestType: PropTypes.string.isRequired,
      canUpdate: PropTypes.shape({
        value: PropTypes.bool.isRequired,
      }),
      billingCard: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
      length: PropTypes.number.isRequired,
      organization: PropTypes.shape({
        overtimeConfig: PropTypes.shape({
          maxRegularHours: PropTypes.number.isRequired,
        }),
      }),
    }),
    timeoffDescription: PropTypes.string,
    worksProfile: PropTypes.shape({ id: PropTypes.string.isRequired }),
  }),
  onFinish: PropTypes.func,
  onLoading: PropTypes.func,
  onRateChange: PropTypes.func,
  setIsFormValid: PropTypes.func.isRequired,
  initialRate: PropTypes.number.isRequired,
};

OfferForm.fragments = {
  staffingRequestInfo: gql`
    fragment StaffingRequestMakeOfferInfo on StaffingRequestApplication {
      id
      canExtendOfferWeekAssignment {
        value
      }
      canIncreaseOfferRate {
        value
      }
      canSubmit {
        value
      }
      worksProfile {
        id
      }

      staffingRequest {
        id
        externalId
        startDate
        endDate
        billingRegularRate
        billingCard {
          ...BillingCardFragment
        }
        length
        shiftType
        shiftsPerWeek
        hoursPerShift
        staffingRequestType
        submittedStaffingRequestApplicationsCount
        inReviewStaffingRequestApplicationsCount
        offeredOffersStaffingRequestApplicationsCount
        acceptedStaffingRequestApplicationsCount
        withdrawnStaffingRequestApplicationsCount
        rejectedStaffingRequestApplicationsCount
        canUpdate {
          value
        }
        role {
          id
          abbreviation
        }
        organization {
          id
          overtimeConfig {
            id
            maxRegularHours
          }
          organizationSetting {
            id
            timekeepingEnabled
          }
        }
        organizationUnit {
          id
          department {
            id
            name
            organizationBusiness {
              id
              name
              city
              stateCode
            }
          }
        }
      }
    }
    ${BillingCardFragment}
  `,
};
